import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    InstitutionTypeBreadcrumbs,
    InstitutionTypeCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { SettingsInstitutionsSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as InstitutionTypeActions from "../../../actions/institutionTypes";
import * as NavigationActions from "../../../actions/navigation";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { InstitutionLookupsLayout } from "../../index";

import "./InstitutionTypePage.scss";

interface InstitutionTypePageProps {
    form: Dtos.InstitutionType & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    institutionType: Dtos.InstitutionType;
    lookups: Dtos.Lookup[];
    loadingInstitutionType: boolean;
    loadInstitutionTypeFailure: boolean;
    loadInstitutionTypeErrorCode: string;
    savingInstitutionType: boolean;
    saveInstitutionTypeSuccess: boolean;
    saveInstitutionTypeFailure: boolean;
    creatingInstitutionType: boolean;
    createInstitutionTypeSuccess: boolean;
    validatingInstitutionType: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearInstitutionType: InstitutionTypeActions.IInstitutionTypeClearActionCreator;
    loadInstitutionType: InstitutionTypeActions.IInstitutionTypeLoadByIdActionCreator;
    saveInstitutionType: InstitutionTypeActions.IInstitutionTypeSaveActionCreator;
    createInstitutionType: InstitutionTypeActions.IInstitutionTypeCreateActionCreator;
    validateInstitutionType: InstitutionTypeActions.IInstitutionTypeValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.institutionType";

const InstitutionTypePage: FC<InstitutionTypePageProps> = ({
    form,
    formState,
    formProperties,
    institutionType,
    lookups,
    loadingInstitutionType,
    loadInstitutionTypeFailure,
    loadInstitutionTypeErrorCode,
    savingInstitutionType,
    saveInstitutionTypeSuccess,
    saveInstitutionTypeFailure,
    creatingInstitutionType,
    createInstitutionTypeSuccess,
    validatingInstitutionType,
    validationFailures,
    permissions,
    clearInstitutionType,
    loadInstitutionType,
    saveInstitutionType,
    createInstitutionType,
    validateInstitutionType,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { institutionTypeId } = useParams<{ institutionTypeId: string }>();
    const institutionTypeIdNumber = parseInt(institutionTypeId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.InstitutionType = {} as Dtos.InstitutionType;

        loadForm(
            reduxFormName,
            Object.assign({ ...institutionType }, extraData)
        );
    };


    // Load institution type
    useEffect(() => {
        if (
            institutionTypeIdNumber &&
            !institutionType &&
            !loadingInstitutionType
        ) {
            setDrawerItems(SettingsInstitutionsSubMenu(permissions));
            setBreadcrumbs(InstitutionTypeBreadcrumbs(institutionTypeIdNumber));
            loadInstitutionType(institutionTypeIdNumber);
        } else if (
            !institutionTypeIdNumber &&
            !creatingInstitutionType &&
            !createInstitutionTypeSuccess
        ) {
            createInstitutionType();
            setBreadcrumbs(InstitutionTypeCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (institutionType && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        institutionTypeIdNumber,
        institutionType,
        setDrawerItems,
        SettingsInstitutionsSubMenu,
        permissions,
        setBreadcrumbs,
        InstitutionTypeBreadcrumbs,
        loadInstitutionType,
        createInstitutionType,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingInstitutionType,
        creatingInstitutionType,
        createInstitutionTypeSuccess,
        InstitutionTypeCreateBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadInstitutionTypeFailure) {
            navigate(`/error/${loadInstitutionTypeErrorCode}`);
        }
    }, [loadInstitutionTypeFailure, loadInstitutionTypeErrorCode, navigate]);

    // Update breadcrumbs
    useEffect(() => {
        if (institutionTypeIdNumber && institutionType?.value) {
            setBreadcrumbs(
                InstitutionTypeBreadcrumbs(
                    institutionTypeIdNumber,
                    institutionType?.value
                )
            );
        }
    }, [
        institutionTypeIdNumber,
        institutionType,
        setBreadcrumbs,
        InstitutionTypeBreadcrumbs,
    ]);

    // Reload site location after saving
    useEffect(() => {
        if (!savingInstitutionType && saveInstitutionTypeSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Institution type saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = "/settings/institution/institutiontypes";
                navigate(url);
            } else if (!institutionTypeIdNumber) {
                let url: string = `/settings/institution/institutiontypes/${institutionType?.id}`;
                navigate(url);
                clearInstitutionType();
            }
        }
    }, [
        savingInstitutionType,
        saveInstitutionTypeSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingInstitutionType && saveInstitutionTypeFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingInstitutionType, saveInstitutionTypeFailure]);

    // Cleanup effect to clear the institution type on component unmount
    useEffect(() => {
        return () => {
            clearInstitutionType();
            resetForm(reduxFormName);
        };
    }, [clearInstitutionType, resetForm, reduxFormName]);

    const onCancel = () => {
        let url: string = "/settings/institution/institutiontypes";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.InstitutionType) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.InstitutionType = {} as Dtos.InstitutionType;

            await saveInstitutionType(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.InstitutionType = {} as Dtos.InstitutionType;

            if (form && hasBeenSubmitted && !validatingInstitutionType) {
                await validateInstitutionType(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.InstitutionTypesUpdate,
                Dtos.Permission.InstitutionTypesCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingInstitutionType ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "institutionType",
                                        {
                                            value: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "institutionType",
                                        {
                                            rank: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "institutionType",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "institutionType",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingInstitutionType,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
    ]);

    return (
        <InstitutionLookupsLayout
            loading={loadingInstitutionType}
            permission={Dtos.Permission.InstitutionTypesView}
        >
            {renderContent}
        </InstitutionLookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<InstitutionTypePageProps> => {
    let institutionType: Dtos.InstitutionType | undefined = !(
        state.institutionTypes.formData instanceof Array
    )
        ? state.institutionTypes.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        institutionType: institutionType!,
        loadingInstitutionType:
            state.institutionTypes.loadState &&
            state.institutionTypes.loadState.status == RequestState.Pending,
        loadInstitutionTypeFailure:
            state.institutionTypes.loadState &&
            state.institutionTypes.loadState.status == RequestState.Failure,
        loadInstitutionTypeErrorCode:
            state.institutionTypes.loadState.errorCode,
        savingInstitutionType:
            state.institutionTypes.saveState &&
            state.institutionTypes.saveState.status == RequestState.Pending,
        saveInstitutionTypeSuccess:
            state.institutionTypes.saveState &&
            state.institutionTypes.saveState.status == RequestState.Success,
        saveInstitutionTypeFailure:
            state.institutionTypes.saveState &&
            state.institutionTypes.saveState.status == RequestState.Failure,
        creatingInstitutionType:
            state.institutionTypes.createState &&
            state.institutionTypes.createState.status == RequestState.Pending,
        createInstitutionTypeSuccess:
            state.institutionTypes.createState &&
            state.institutionTypes.createState.status == RequestState.Success,
        validatingInstitutionType:
            state.institutionTypes.validateState &&
            state.institutionTypes.validateState.status == RequestState.Pending,
        permissions: permissions,
        lookups: [],
        form: state.reduxForms.institutionType,
        formState: state.institutionTypes.formState,
        formProperties: state.institutionTypes.formProperties,
        validationFailures: institutionType
            ? state.institutionTypes.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<InstitutionTypePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadInstitutionType: bindActionCreators(
            InstitutionTypeActions.LoadInstitutionTypeById,
            dispatch
        ),
        clearInstitutionType: bindActionCreators(
            InstitutionTypeActions.Clear,
            dispatch
        ),
        saveInstitutionType: bindActionCreators(
            InstitutionTypeActions.SaveInstitutionType,
            dispatch
        ),
        createInstitutionType: bindActionCreators(
            InstitutionTypeActions.CreateInstitutionType,
            dispatch
        ),
        validateInstitutionType: bindActionCreators(
            InstitutionTypeActions.ValidateInstitutionType,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(InstitutionTypePage);
